import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CheckOrder, Maintenance } from 'src/app/models/maintenance/maintenance.model';
import { AuthService } from '../../authentication/auth.service';
import { BaseApiService } from '../../base-api.service';
import { TranslateFirebaseService } from '../../utils/translate-firebase.service';

@Injectable()
export class RestMaintenanceService extends BaseApiService {

  constructor(httpClient: HttpClient,
    translate : TranslateFirebaseService,
    private authService : AuthService) {
    super(httpClient, translate);
  }

  /**
   * GET /Maintenance/{maintenanceId}
   * Retrieves a maintenance by ID
   */
  getMaintenanceById(maintenanceId: number, languageId: string, defaultLanguage: number = 0): Observable<Maintenance> {
    const params = new HttpParams().set('defaultLanguage', defaultLanguage.toString());
    const headers = this.authService.upsHttpHeaders.set('x-language-id', languageId);
    return this.httpClient.get<Maintenance>(
      super.getUpsApiHost() + "Maintenance/" + maintenanceId, 
      { headers, params }
    );
  }

  /**
   * GET /Maintenance/article/{articleId}
   * Retrieves maintenances by article ID
   */
  getMaintenancesByArticleId(articleId: number, languageId: string, defaultLanguage: number = 0): Observable<Maintenance[]> {
    const params = new HttpParams().set('defaultLanguage', defaultLanguage.toString());
    const headers = this.authService.upsHttpHeaders.set('x-language-id', languageId);
    return this.httpClient.get<Maintenance[]>(
      super.getUpsApiHost() + "Maintenance/article/" + articleId,
      { headers, params }
    );
  }

  /**
   * GET /Maintenance/{articleId}/{languageId}
   * Retrieves maintenances by article ID and language ID (Admin only)
   */
  getMaintenancesByArticleIdAndLanguageId(articleId: number, languageId: string, defaultLanguage: number = 0): Observable<Maintenance[]> {
    const params = new HttpParams().set('defaultLanguage', defaultLanguage.toString());
    return this.httpClient.get<Maintenance[]>(
      super.getUpsApiHost() + "Maintenance/" + articleId + "/" + languageId, 
      { headers: this.authService.upsHttpHeaders, params }
    );
  }

  /**
   * POST /Maintenance
   * Creates a new maintenance (Admin only)
   */
  createMaintenance(maintenance: Maintenance): Observable<Maintenance> {
    return this.httpClient.post<Maintenance>(
      super.getUpsApiHost() + "Maintenance", 
      maintenance, 
      { headers: this.authService.upsHttpHeaders }
    );
  }

  /**
   * PUT /Maintenance/{maintenanceId}
   * Updates an existing maintenance (Admin only)
   */
  updateMaintenance(maintenanceId: number, maintenance: Maintenance): Observable<Maintenance> {
    return this.httpClient.put<Maintenance>(
      super.getUpsApiHost() + "Maintenance/" + maintenanceId, 
      maintenance, 
      { headers: this.authService.upsHttpHeaders }
    );
  }

  /**
   * PUT /Maintenance/orderchecks
   * Updates the order of maintenance checks (Admin only)
   */
  updateMaintenanceChecksOrder(orderArray: CheckOrder[]): Observable<any> {
    return this.httpClient.put(
      super.getUpsApiHost() + "Maintenance/orderchecks", 
      orderArray, 
      { headers: this.authService.upsHttpHeaders }
    );
  }

  /**
   * DELETE /Maintenance/{maintenanceId}
   * Deletes a maintenance (Admin only)
   */
  deleteMaintenance(maintenanceId: number): Observable<any> {
    return this.httpClient.delete(
      super.getUpsApiHost() + "Maintenance/" + maintenanceId, 
      { headers: this.authService.upsHttpHeaders }
    );
  }

  /**
   * POST /Maintenance/clone/contents/{languageId}
   * Clones maintenance content translations (Admin only)
   */
  cloneMaintenanceContents(request: any, languageId: string): Observable<any> {
    return this.httpClient.post<any>(
      super.getUpsApiHost() + "Maintenance/clone/contents/" + languageId,
      request,
      { headers: this.authService.upsHttpHeaders }
    );
  }

  /**
   * Legacy method - kept for backward compatibility
   * @deprecated Use getMaintenancesByArticleIdAndLanguageId instead
   */
  orderChecks(orderArray: CheckOrder[]): Observable<boolean> {
    return this.httpClient.put<boolean>(
      super.getUpsApiHost() + "Maintenance/orderchecks", 
      orderArray, 
      { headers: this.authService.upsHttpHeaders }
    );
  }

  /**
   * Legacy method - kept for backward compatibility
   */
  getMaintenanceHistoryById(maintenanceHistoryId: number): Observable<any> {
    return this.httpClient.get<any>(
      super.getUpsApiHost() + '​/api​/MaintenanceHistory​/' + maintenanceHistoryId, 
      { headers: this.authService.upsHttpHeaders }
    );
  }
}
